import { useEffect, useRef, useCallback, RefObject } from 'react';

/**
 * Hook pour créer un piège de focus dans un conteneur
 * Essentiel pour l'accessibilité des modales et dialogues
 *
 * @param isActive - Active ou désactive le piège de focus
 * @param options - Options de configuration
 * @returns Ref à attacher au conteneur
 */
export interface UseFocusTrapOptions {
  /** Élément à focus en premier (par défaut: premier élément focusable) */
  initialFocusRef?: RefObject<HTMLElement>;
  /** Élément à focus à la fermeture (par défaut: élément qui avait le focus avant) */
  returnFocusRef?: RefObject<HTMLElement>;
  /** Désactiver le retour du focus à la fermeture */
  disableReturnFocus?: boolean;
  /** Callback quand on tente de sortir du piège */
  onEscapeAttempt?: () => void;
}

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]'
].join(', ');

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  isActive: boolean,
  options: UseFocusTrapOptions = {}
): RefObject<T> {
  const containerRef = useRef<T>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Sauvegarde l'élément actuellement focusé
  useEffect(() => {
    if (isActive) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [isActive]);

  // Gestion du focus initial
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    // Focus sur l'élément initial ou le premier élément focusable
    const setInitialFocus = () => {
      if (options.initialFocusRef?.current) {
        options.initialFocusRef.current.focus();
      } else {
        const firstFocusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          // Si aucun élément focusable, focus sur le conteneur lui-même
          container.setAttribute('tabindex', '-1');
          container.focus();
        }
      }
    };

    // Délai pour s'assurer que le DOM est prêt
    const timeoutId = setTimeout(setInitialFocus, 10);

    return () => clearTimeout(timeoutId);
  }, [isActive, options.initialFocusRef]);

  // Restauration du focus à la fermeture
  useEffect(() => {
    return () => {
      if (!options.disableReturnFocus) {
        const elementToFocus = options.returnFocusRef?.current || previousFocusRef.current;
        if (elementToFocus && typeof elementToFocus.focus === 'function') {
          // Délai pour s'assurer que la modale est fermée
          setTimeout(() => {
            elementToFocus.focus();
          }, 10);
        }
      }
    };
  }, [options.disableReturnFocus, options.returnFocusRef]);

  // Piège de focus avec Tab et Shift+Tab
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isActive || !containerRef.current) return;

    if (event.key === 'Tab') {
      const container = containerRef.current;
      const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift+Tab sur le premier élément -> aller au dernier
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // Tab sur le dernier élément -> aller au premier
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }, [isActive]);

  // Empêche le focus de sortir du conteneur
  const handleFocusOut = useCallback((event: FocusEvent) => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    // Si le focus sort du conteneur, le ramener dedans
    if (relatedTarget && !container.contains(relatedTarget)) {
      event.preventDefault();
      const firstFocusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
      if (firstFocusable) {
        firstFocusable.focus();
      }
      options.onEscapeAttempt?.();
    }
  }, [isActive, options]);

  // Attache les écouteurs d'événements
  useEffect(() => {
    if (!isActive) return;

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isActive, handleKeyDown, handleFocusOut]);

  return containerRef;
}

export default useFocusTrap;
